import { TenantData } from './types'
import { TenantActionTypes } from './actionTypes'

export interface TenantsDataState {
  selectedTenant: TenantData | null
  allTenants: Record<string, TenantData>
  allIds: string[]
  selectedTenantId?: string
  isLoading: boolean
  error: string | null
}

const initialState: TenantsDataState = {
  selectedTenant: null,
  allTenants: {},
  allIds: [],
  selectedTenantId: undefined,
  isLoading: false,
  error: null,
}

export const reducer = (
  state: TenantsDataState = initialState,
  action: { type: string; payload?: any }
): TenantsDataState => {
  switch (action.type) {
    case TenantActionTypes.SET_TENANT: {
      const tenantData = action.payload as TenantData
      const tenantId =
        tenantData.id || tenantData.partnerId || (tenantData as any).partner_id

      if (!tenantId) {
        console.error('Tenant data must have an id or partnerId', tenantData)
        return state
      }

      return {
        ...state,
        selectedTenant: tenantData,
        allTenants: {
          ...state.allTenants,
          [tenantId]: tenantData,
        },
        allIds: state.allIds.includes(tenantId)
          ? state.allIds
          : [...state.allIds, tenantId],
        selectedTenantId: tenantId,
        isLoading: false,
        error: null,
      }
    }

    case TenantActionTypes.SET_CURRENT_TENANT_ID: {
      const tenantId = action.payload
      return {
        ...state,
        selectedTenantId: tenantId,
        selectedTenant: state.allTenants[tenantId] || null,
      }
    }

    case TenantActionTypes.UPDATE_TENANT: {
      const tenantData = action.payload as Partial<TenantData>
      const selectedTenantId = state.selectedTenantId

      if (!selectedTenantId || !state.selectedTenant) {
        return state
      }

      const updatedTenant = {
        ...state.selectedTenant,
        ...tenantData,
      }

      return {
        ...state,
        selectedTenant: updatedTenant,
        allTenants: {
          ...state.allTenants,
          [selectedTenantId]: updatedTenant,
        },
        isLoading: false,
        error: null,
      }
    }

    case TenantActionTypes.TENANT_LOADING: {
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    }

    case TenantActionTypes.GET_TENANT_FAILED: {
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      }
    }

    case TenantActionTypes.SET_AVAILABLE_TENANTS: {
      const tenants = action.payload as TenantData[]
      const tenantMap: Record<string, TenantData> = {}
      const tenantIds: string[] = []

      tenants.forEach(tenant => {
        const tenantId =
          tenant.id || tenant.partnerId || (tenant as any).partner_id
        if (tenantId) {
          tenantMap[tenantId] = tenant
          tenantIds.push(tenantId)
        }
      })

      const selectedTenantId = state.selectedTenantId || tenantIds[0]
      const selectedTenant = selectedTenantId
        ? tenantMap[selectedTenantId]
        : null

      return {
        ...state,
        allTenants: {
          ...state.allTenants,
          ...tenantMap,
        },
        selectedTenant,
        selectedTenantId,
        allIds: tenantIds,
        isLoading: false,
        error: null,
      }
    }

    case TenantActionTypes.CLEAR_TENANT: {
      return {
        ...initialState,
      }
    }

    case TenantActionTypes.CLEAR_ALL_TENANTS: {
      return {
        ...state,
        selectedTenant: null,
        allTenants: {},
        allIds: [],
        selectedTenantId: undefined,
        isLoading: false,
        error: null,
      }
    }

    case TenantActionTypes.TENANT_CLEANUP: {
      return {
        ...state,
        isLoading: false,
        error: null,
      }
    }

    default:
      return state
  }
}
